/**
 * 🔧 CENTRALIZED ERROR HANDLER
 *
 * Handles all error types across API endpoints with consistent classification
 * and integration with the existing retry mechanism from useArchitectApp.jsx
 *
 * Features:
 * - Standardized error classification (retryable vs non-retryable)
 * - Consistent error response formatting
 * - Integration with existing retry patterns
 * - Extensible for future error types and platforms
 * - User-friendly error messages
 */

/**
 * 🔒 SECURITY: HTML escape helper to prevent XSS attacks
 * Escapes HTML special characters in user-controllable content
 */
const escapeHtml = (text) => {
    if (typeof text !== 'string') return '';
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
};

// ===== ERROR TYPES & CLASSIFICATION =====

export const ERROR_TYPES = {
  // Retryable errors (API overloads, parsing issues)
  API_OVERLOAD: 'api_overload',
  PARSING_ERROR: 'parsing_error',
  RESPONSE_TRUNCATION: 'response_truncation',
  TIMEOUT_ERROR: 'timeout_error',

  // Non-retryable errors (user/system issues)
  VALIDATION_ERROR: 'validation_error',
  PERMISSION_ERROR: 'permission_error',
  SESSION_EXPIRED: 'session_expired',  // Session expired - requires re-login
  QUOTA_EXCEEDED: 'quota_exceeded',
  INSUFFICIENT_CREDITS: 'insufficient_credits',
  INVALID_INPUT: 'invalid_input',

  // System errors
  NETWORK_ERROR: 'network_error',
  UNKNOWN_ERROR: 'unknown_error'
};

export const RETRY_CATEGORIES = {
  RETRYABLE: 'retryable',
  NON_RETRYABLE: 'non_retryable',
  SYSTEM_ERROR: 'system_error'
};

// ===== ERROR DETECTION PATTERNS =====

const ERROR_PATTERNS = {
  [ERROR_TYPES.API_OVERLOAD]: [
    '529',                          // HTTP 529 Service Unavailable
    'overloaded',                   // "Overloaded" message
    'overloaded_error',            // Claude API error type
    'claude api error (529)',      // Specific Claude 529 error
    'service_unavailable',         // Service unavailable
    'rate_limit',                  // Rate limiting
    'too_many_requests',           // 429 errors
    'server_overloaded',           // Server overload
    'capacity_exceeded',           // Capacity issues
    'temporarily_unavailable',     // Temporary unavailability
    'failed to generate analysis', // Analysis-specific failure
    'failed to generate research', // Research-specific failure
    'failed to generate prompts'   // Prompts-specific failure
  ],
  
  [ERROR_TYPES.PARSING_ERROR]: [
    'mismatched braces',           // JSON brace mismatch
    'unexpected end of json',      // Truncated JSON
    'failed to parse',             // General parsing failure
    'invalid json',                // Invalid JSON format
    'json parse error',            // JSON parsing error
    'enhanced ai response',        // Our specific parsing error
    'failed to parse enhanced ai', // Our enhanced AI parsing error
    'syntax error',                // JSON syntax errors
    'unexpected token',            // JSON token errors
    'no json found',               // No JSON in response
    'invalid format'               // Invalid response format
  ],
  
  [ERROR_TYPES.RESPONSE_TRUNCATION]: [
    'unmatched braces',            // Incomplete JSON structure
    'mismatched braces',           // Also add this variant for better matching
    'response truncated',          // Explicit truncation
    'incomplete response',         // Incomplete data
    'partial json',                // Partially received JSON
    'connection terminated'        // Connection issues during response
  ],
  
  [ERROR_TYPES.TIMEOUT_ERROR]: [
    'timeout',                     // General timeout
    'request timeout',             // Request timeout
    'server timeout',              // Server timeout
    'connection timeout',          // Connection timeout
    'taking too long to respond'   // Our timeout message
  ],
  
  [ERROR_TYPES.VALIDATION_ERROR]: [
    'invalid request data',        // Input validation
    'required field missing',      // Missing required data
    'invalid platform',            // Platform validation
    'schema validation',           // Schema errors
    'invalid input',               // General input issues
    'must be at least',            // Length validation (e.g., "must be at least 10 characters")
    'too short',                   // Length validation
    'too long',                    // Length validation  
    'must contain',                // Content validation
    'invalid characters',          // Character validation
    'required field',              // Field requirement validation
    'cannot be empty',             // Empty field validation
    'invalid format',              // Format validation
    'validation failed',           // General validation
    'validation error'             // Explicit validation error
  ],
  
  [ERROR_TYPES.QUOTA_EXCEEDED]: [
    'quota exceeded',              // API quota
    'usage limit',                 // Usage limits
    'monthly limit',               // Monthly quotas
    'billing required'             // Billing issues
  ],
  
  [ERROR_TYPES.INSUFFICIENT_CREDITS]: [
    'insufficient credits',        // Our specific error
    'insufficient_credits',        // API error type
    'payment required',            // HTTP 402 message
    '402',                         // HTTP 402 status
    'not enough credits',          // Alternative phrasing
    'credit balance too low',      // Balance check
    'credits required',            // Credits needed
    'low credit balance'           // Low balance warning
  ],
  
  [ERROR_TYPES.SESSION_EXPIRED]: [
    'invalid or expired session',  // Backend session validation error
    'session expired',             // Explicit session expiry
    'session invalid',             // Invalid session
    'expired session',             // Alternative phrasing
    'session has expired',         // Full phrase
    'token expired',               // JWT token expired
    'jwt expired',                 // JWT specific
    'session not found'            // Session doesn't exist
  ],

  [ERROR_TYPES.PERMISSION_ERROR]: [
    'unauthorized',                // 401 errors (general)
    'forbidden',                   // 403 errors
    'access denied',               // Permission denied
    'invalid api key',             // API key issues
    'authentication failed'        // Auth failures
  ]
};

// ===== ERROR CLASSIFICATION UTILITY =====

/**
 * Classify an error based on its message and characteristics
 * @param {Error|Object} error - The error to classify
 * @returns {Object} Error classification details
 */
export function classifyError(error) {
  const errorMessage = error.message?.toLowerCase() || '';
  const errorString = error.toString?.()?.toLowerCase() || '';
  const errorDetails = `${errorMessage} ${errorString}`;
  
  // Check each error type against patterns
  for (const [errorType, patterns] of Object.entries(ERROR_PATTERNS)) {
    const matches = patterns.some(pattern => 
      errorDetails.includes(pattern.toLowerCase())
    );
    
    if (matches) {
      return {
        type: errorType,
        category: getErrorCategory(errorType),
        isRetryable: isRetryableErrorType(errorType),
        service: detectService(errorDetails),
        patterns: patterns.filter(p => errorDetails.includes(p.toLowerCase()))
      };
    }
  }
  
  // Default to unknown error
  return {
    type: ERROR_TYPES.UNKNOWN_ERROR,
    category: RETRY_CATEGORIES.SYSTEM_ERROR,
    isRetryable: false,
    service: 'Unknown',
    patterns: []
  };
}

/**
 * Get error category for an error type
 */
function getErrorCategory(errorType) {
  const retryableTypes = [
    ERROR_TYPES.API_OVERLOAD,
    ERROR_TYPES.PARSING_ERROR,
    ERROR_TYPES.RESPONSE_TRUNCATION,
    ERROR_TYPES.TIMEOUT_ERROR
  ];
  
  const nonRetryableTypes = [
    ERROR_TYPES.VALIDATION_ERROR,
    ERROR_TYPES.PERMISSION_ERROR,
    ERROR_TYPES.SESSION_EXPIRED,
    ERROR_TYPES.QUOTA_EXCEEDED,
    ERROR_TYPES.INSUFFICIENT_CREDITS,
    ERROR_TYPES.INVALID_INPUT
  ];
  
  if (retryableTypes.includes(errorType)) {
    return RETRY_CATEGORIES.RETRYABLE;
  } else if (nonRetryableTypes.includes(errorType)) {
    return RETRY_CATEGORIES.NON_RETRYABLE;
  } else {
    return RETRY_CATEGORIES.SYSTEM_ERROR;
  }
}

/**
 * Check if an error type is retryable
 */
function isRetryableErrorType(errorType) {
  return getErrorCategory(errorType) === RETRY_CATEGORIES.RETRYABLE;
}

/**
 * Detect which AI service is having issues
 */
function detectService(errorDetails) {
  if (errorDetails.includes('claude')) return 'Claude';
  if (errorDetails.includes('openai') || errorDetails.includes('gpt')) return 'OpenAI';
  if (errorDetails.includes('529') || errorDetails.includes('overloaded_error')) return 'Claude'; // 529 typically Claude
  if (errorDetails.includes('429') || errorDetails.includes('rate_limit')) return 'AI Service';
  // Detect Claude-specific parsing errors
  if (errorDetails.includes('claude response') || errorDetails.includes('claude prompts')) return 'Claude';
  // Detect OpenAI-specific parsing errors  
  if (errorDetails.includes('openai response') || errorDetails.includes('openai prompts')) return 'OpenAI';
  return 'AI Service';
}

// ===== USER-FRIENDLY ERROR MESSAGES =====

const ERROR_MESSAGES = {
  [ERROR_TYPES.API_OVERLOAD]: {
    user: 'AI service is temporarily overloaded. Please try again in a moment.',
    technical: 'API service experiencing high load',
    action: 'retry'
  },
  
  [ERROR_TYPES.PARSING_ERROR]: {
    user: 'AI response was incomplete or corrupted. Please try again.',
    technical: 'Failed to parse AI response - likely truncated or malformed',
    action: 'retry'
  },
  
  [ERROR_TYPES.RESPONSE_TRUNCATION]: {
    user: 'AI response was cut off. Please try again.',
    technical: 'Response truncated during transmission',
    action: 'retry'
  },
  
  [ERROR_TYPES.TIMEOUT_ERROR]: {
    user: 'AI analysis timed out after 5 minutes. This can happen during high demand. Please try again.',
    technical: 'Request exceeded 5-minute timeout limit',
    action: 'retry'
  },
  
  [ERROR_TYPES.VALIDATION_ERROR]: {
    user: 'Please check your input and try again.',
    technical: 'Input validation failed',
    action: 'fix'
  },
  
  [ERROR_TYPES.PERMISSION_ERROR]: {
    user: 'Access denied. Please check your credentials.',
    technical: 'Authentication or authorization failed',
    action: 'fix'
  },

  [ERROR_TYPES.SESSION_EXPIRED]: {
    user: 'Your session has expired. Please sign in again.',
    technical: 'Session token expired or invalid',
    action: 'logout'
  },
  
  [ERROR_TYPES.QUOTA_EXCEEDED]: {
    user: 'API usage limit exceeded. Please try again later.',
    technical: 'API quota or usage limit reached',
    action: 'wait'
  },
  
  [ERROR_TYPES.INSUFFICIENT_CREDITS]: {
    user: 'You don\'t have enough credits to complete this operation.',
    technical: 'Insufficient credit balance for requested operation',
    action: 'purchase'
  },
  
  [ERROR_TYPES.INVALID_INPUT]: {
    user: 'Invalid input provided. Please check your data.',
    technical: 'Input validation failed',
    action: 'fix'
  },
  
  [ERROR_TYPES.NETWORK_ERROR]: {
    user: 'Network error. Please check your connection.',
    technical: 'Network connectivity issue',
    action: 'retry'
  },
  
  [ERROR_TYPES.UNKNOWN_ERROR]: {
    user: 'An unexpected error occurred. Please try again.',
    technical: 'Unknown error type',
    action: 'retry'
  }
};

/**
 * Get user-friendly error message
 * @param {string} errorType - The classified error type
 * @param {string} service - The service name
 * @param {string} originalMessage - The original error message (optional)
 */
export function getErrorMessage(errorType, service = 'AI Service', originalMessage = '') {
  const message = ERROR_MESSAGES[errorType] || ERROR_MESSAGES[ERROR_TYPES.UNKNOWN_ERROR];
  
  // For validation errors, use the original message if it's descriptive and user-friendly
  if (errorType === ERROR_TYPES.VALIDATION_ERROR && originalMessage) {
    // Check if original message is descriptive (has helpful validation info)
    const isDescriptive = originalMessage.includes('must be') || 
                         originalMessage.includes('required') ||
                         originalMessage.includes('invalid') ||
                         originalMessage.includes('too short') ||
                         originalMessage.includes('too long') ||
                         originalMessage.includes('characters') ||
                         originalMessage.includes('cannot be empty');
    
    if (isDescriptive && originalMessage.length < 200) { // Not too long
      return {
        ...message,
        user: originalMessage, // Use the descriptive validation message
        technical: `${message.technical}: ${originalMessage}`
      };
    }
  }
  
  return {
    ...message,
    user: message.user.replace('AI service', service).replace('AI Service', service)
  };
}

// ===== INTEGRATION WITH EXISTING RETRY MECHANISM =====

/**
 * Check if error is retryable (compatible with existing isRetryableError function)
 * This maintains compatibility with the existing retry mechanism in useArchitectApp.jsx
 */
export function isRetryable(error) {
  const classification = classifyError(error);
  return classification.isRetryable;
}

/**
 * Get service name for overload detection (compatible with existing getOverloadedService)
 */
export function getOverloadedService(error) {
  const classification = classifyError(error);
  return classification.service;
}

// ===== STANDARDIZED ERROR RESPONSE FORMATTER =====

/**
 * Create standardized error response for API endpoints
 * @param {Error} error - The original error
 * @param {string} step - The step where error occurred (research, validation, analysis, prompts)
 * @param {string} platform - The platform being used
 * @returns {Object} Standardized error response
 */
export function createErrorResponse(error, step, platform = 'unknown') {
  const classification = classifyError(error);
  const errorMessage = getErrorMessage(classification.type, classification.service, error.message);
  
  const baseResponse = {
    success: false,
    error: error.message || 'Unknown error occurred',
    errorType: classification.type,
    errorCategory: classification.category,
    isRetryable: classification.isRetryable,
    service: classification.service,
    step: step,
    platform: platform,
    userMessage: errorMessage.user,
    technicalMessage: errorMessage.technical,
    recommendedAction: errorMessage.action,
    timestamp: new Date().toISOString()
  };
  
  // Add retry-specific info for retryable errors
  if (classification.isRetryable) {
    baseResponse.retryInfo = {
      canRetry: true,
      retryDelay: getRetryDelay(classification.type),
      maxRetries: getMaxRetries(classification.type),
      retryStrategy: getRetryStrategy(classification.type)
    };
  }
  
  // Add debugging info in development only
  if (import.meta.env.DEV) {
    baseResponse.debug = {
      originalError: error.toString(),
      errorStack: error.stack,
      detectedPatterns: classification.patterns,
      classification: classification
    };
  }
  
  return baseResponse;
}

/**
 * Get recommended retry delay for error type
 */
function getRetryDelay(errorType) {
  switch (errorType) {
    case ERROR_TYPES.API_OVERLOAD:
      return 5000; // 5 seconds for overloads
    case ERROR_TYPES.TIMEOUT_ERROR:
      return 10000; // 10 seconds for timeouts (longer wait before retry)
    case ERROR_TYPES.PARSING_ERROR:
    case ERROR_TYPES.RESPONSE_TRUNCATION:
      return 2000; // 2 seconds for parsing issues
    default:
      return 1000; // 1 second default
  }
}

/**
 * Get max retries for error type
 */
function getMaxRetries(errorType) {
  switch (errorType) {
    case ERROR_TYPES.API_OVERLOAD:
      return 3; // More retries for overloads
    case ERROR_TYPES.TIMEOUT_ERROR:
      return 2; // Fewer retries for timeouts
    default:
      return 2; // Default retries
  }
}

/**
 * Get retry strategy for error type
 */
function getRetryStrategy(errorType) {
  switch (errorType) {
    case ERROR_TYPES.API_OVERLOAD:
      return 'exponential_backoff';
    case ERROR_TYPES.TIMEOUT_ERROR:
      return 'linear_backoff';
    default:
      return 'immediate';
  }
}

// ===== LOGGING UTILITIES =====

/**
 * Enhanced error logging with classification details
 */
export function logError(error, step, platform, context = {}) {
  const classification = classifyError(error);
  
  return classification;
}

// ===== ENHANCED ERROR PROCESSING FOR FRONTEND =====

/**
 * Process backend error response into enhanced error object (like original API)
 * This matches the pattern from public/js/utils/api.js
 */
export function processBackendError(errorData, endpoint) {
  if (!errorData) return null;
  
  // Handle enhanced backend error format (matching original pattern)
  if (errorData.userMessage) {
    const enhancedError = new Error(errorData.userMessage);
    enhancedError.details = errorData.details;
    enhancedError.errorType = errorData.errorType;
    enhancedError.retryable = errorData.retryable !== false; // Default to retryable
    enhancedError.step = errorData.step;
    enhancedError.platform = errorData.platform;
    enhancedError.originalError = errorData.error;
    enhancedError.isRetryable = errorData.retryable !== false;
    enhancedError.userMessage = errorData.userMessage;
    
    // Detect service from error details for better error attribution
    const errorDetails = `${errorData.error || ''} ${errorData.details || ''}`.toLowerCase();
    enhancedError.service = detectService(errorDetails);
    
    enhancedError._isEnhancedBackendError = true;
    return enhancedError;
  }
  
  return null;
}

/**
 * Enhanced error classification for frontend (like original showEnhancedError)
 */
export function classifyFrontendError(error, fallbackMessage = 'An error occurred') {
  // Check if it's already an enhanced backend error
  if (error._isEnhancedBackendError) {
    return {
      isEnhanced: true,
      userMessage: error.userMessage,
      errorType: error.errorType,
      isRetryable: error.retryable,
      step: error.step,
      platform: error.platform,
      details: error.details,
      originalError: error.originalError,
      icon: getErrorIcon(error.errorType),
      retryText: error.retryable ? 'Please try again.' : 'Please check your input.'
    };
  }
  
  // Process through centralized classification
  const classification = classifyError(error);
  const errorMessage = getErrorMessage(classification.type, classification.service, error.message);
  
  return {
    isEnhanced: false,
    userMessage: errorMessage.user || error.message || fallbackMessage,
    errorType: classification.type,
    isRetryable: classification.isRetryable,
    step: 'unknown',
    platform: 'unknown',
    details: error.message,
    originalError: error,
    icon: getErrorIcon(classification.type),
    retryText: classification.isRetryable ? 'Please try again.' : 'Please check your input.'
  };
}

/**
 * Get error icon based on error type (matching original app.js pattern)
 */
export function getErrorIcon(errorType) {
  switch (errorType) {
    case 'api_error':
    case ERROR_TYPES.API_OVERLOAD:
      return '🔌';
    case 'parsing_error':
    case ERROR_TYPES.PARSING_ERROR:
    case ERROR_TYPES.RESPONSE_TRUNCATION:
      return '🤖';
    case 'validation_error':
    case ERROR_TYPES.VALIDATION_ERROR:
    case ERROR_TYPES.INVALID_INPUT:
      return '⚠️';
    case ERROR_TYPES.TIMEOUT_ERROR:
      return '⏱️';
    case ERROR_TYPES.NETWORK_ERROR:
      return '🌐';
    case ERROR_TYPES.PERMISSION_ERROR:
      return '🔒';
    case ERROR_TYPES.SESSION_EXPIRED:
      return '⏰';
    case ERROR_TYPES.QUOTA_EXCEEDED:
      return '📊';
    case ERROR_TYPES.INSUFFICIENT_CREDITS:
      return '💳';
    default:
      return '❌';
  }
}

/**
 * Show enhanced error UI (matching original showAnalysisError pattern)
 */
export function showEnhancedErrorUI(error, fallbackMessage, options = {}) {
  const {
    errorStateId = 'errorState',
    errorMessageId = 'errorMessage', 
    errorDetailsId = 'errorDetails',
    retryButtonId = 'retryAnalysisBtn',
    onRetry = null
  } = options;
  
  const classification = classifyFrontendError(error, fallbackMessage);
  
  
  // Hide loading states
  document.getElementById('loadingState')?.classList.add('hidden');
  document.getElementById('analysisResults')?.classList.add('hidden');
  
  // Show error state
  const errorState = document.getElementById(errorStateId);
  const errorMessage = document.getElementById(errorMessageId);
  const errorDetails = document.getElementById(errorDetailsId);
  
  if (errorState) {
    errorState.classList.remove('hidden');
    
    // 🔒 SECURITY: Update error message with HTML-escaped content to prevent XSS
    if (errorMessage) {
      errorMessage.innerHTML = `
        <p class="text-red-600 font-medium">${escapeHtml(classification.icon)} ${escapeHtml(classification.userMessage)}</p>
        <p class="mt-2 text-gray-600">${escapeHtml(classification.retryText)}</p>
        <p class="mt-2 text-sm text-gray-500">Step: ${escapeHtml(classification.step.charAt(0).toUpperCase() + classification.step.slice(1))}</p>
      `;
    }
    
    // Show technical details
    if (errorDetails) {
      errorDetails.textContent = `Error: ${classification.details}\n\nStep: ${classification.step}\nType: ${classification.errorType}\nRetryable: ${classification.isRetryable}\nTime: ${new Date().toISOString()}`;
    }
    
    // Set up retry functionality
    const retryBtn = document.getElementById(retryButtonId);
    if (retryBtn) {
      if (classification.isRetryable && onRetry) {
        retryBtn.style.display = 'inline-flex';
        retryBtn.onclick = () => {
          errorState.classList.add('hidden');
          onRetry();
        };
      } else {
        retryBtn.style.display = 'none';
      }
    }
  }
  
  return classification;
}

/**
 * Show prompts-specific error (matching original showPromptsError pattern)
 */
export function showPromptsError(message, options = {}) {
  const {
    loadingStateId = 'promptsLoadingState',
    resultsId = 'promptsResults',
    errorStateId = 'promptsErrorState'
  } = options;
  
  document.getElementById(loadingStateId)?.classList.add('hidden');
  document.getElementById(resultsId)?.classList.add('hidden');
  
  const errorState = document.getElementById(errorStateId);
  if (errorState) {
    errorState.classList.remove('hidden');
    const errorMsg = errorState.querySelector('p');
    if (errorMsg) {
      errorMsg.textContent = message;
    }
  }
}

/**
 * Create enhanced toast notification (matching original showToast pattern)
 */
export function showToast(message, type = 'success') {
  // Create a toast notification matching original styling
  const toast = document.createElement('div');
  toast.className = `fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 transform transition-transform duration-300 translate-x-full`;
  
  // Style based on type (matching original)
  switch (type) {
    case 'error':
      toast.className += ' bg-red-500 text-white';
      break;
    case 'success':
      toast.className += ' bg-green-500 text-white';
      break;
    case 'warning':
      toast.className += ' bg-yellow-500 text-white';
      break;
    case 'info':
    default:
      toast.className += ' bg-blue-500 text-white';
  }
  
  toast.textContent = message;
  document.body.appendChild(toast);
  
  // Animate in
  setTimeout(() => {
    toast.classList.remove('translate-x-full');
  }, 100);
  
  // Remove after delay
  setTimeout(() => {
    toast.classList.add('translate-x-full');
    setTimeout(() => {
      if (document.body.contains(toast)) {
        document.body.removeChild(toast);
      }
    }, 300);
  }, 3000);
}

// ===== EXPORT FOR BACKEND USE =====

/**
 * Backend error handler - creates consistent error responses for API endpoints
 */
export function handleAPIError(error, step, platform = 'unknown', context = {}) {
  // Log the error with full analysis
  const classification = logError(error, step, platform, context);
  
  // Create standardized response
  const errorResponse = createErrorResponse(error, step, platform);
  
  // Return with appropriate HTTP status
  const httpStatus = getHTTPStatus(classification.type);
  
  return {
    response: errorResponse,
    status: httpStatus,
    classification: classification
  };
}

/**
 * Get appropriate HTTP status code for error type
 */
function getHTTPStatus(errorType) {
  switch (errorType) {
    case ERROR_TYPES.VALIDATION_ERROR:
    case ERROR_TYPES.INVALID_INPUT:
      return 400; // Bad Request
    case ERROR_TYPES.PERMISSION_ERROR:
      return 401; // Unauthorized
    case ERROR_TYPES.QUOTA_EXCEEDED:
      return 429; // Too Many Requests
    case ERROR_TYPES.INSUFFICIENT_CREDITS:
      return 402; // Payment Required
    case ERROR_TYPES.API_OVERLOAD:
    case ERROR_TYPES.TIMEOUT_ERROR:
      return 503; // Service Unavailable
    default:
      return 500; // Internal Server Error
  }
} 